import React, { useEffect, useMemo, useState } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { Player } from '../../types/player';
import { playersAPI, mlszAPI } from '../../services/api';
import type { SportDoctorSyncResult } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { getErrorMessage } from '../../utils/errorMessage';
import { getMedicalState, medicalBadge, MedicalState, useSportDoctorWarningDays } from '../../utils/playerMedical';
import { matchesSearch } from '../../utils/search';
import { useBatchedList } from '../../hooks/useBatchedList';
import LazyLoadTrigger from '../ui/LazyLoadTrigger';

interface SportDoctorCheckModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** A Játékosok oldal szűrt listájának aktív játékosai. */
  players: Player[];
  canModify: boolean;
  /** Mentés után a lista frissítése. */
  onChanged: () => Promise<void> | void;
}

type Segment = 'problem' | 'expiring' | 'valid';

const SEGMENTS: { id: Segment; label: string; states: MedicalState[] }[] = [
  { id: 'problem', label: 'Lejárt vagy hiányzó', states: ['expired', 'missing'] },
  { id: 'expiring', label: 'Hamarosan lejár', states: ['expiring'] },
  { id: 'valid', label: 'Érvényes', states: ['valid'] },
];

/** A SportDoctorSyncOutcome enum sorszámai a backend sorrendjében. */
const SYNC_OUTCOMES: { outcome: number; label: string; tone: string }[] = [
  { outcome: 0, label: 'frissült', tone: 'text-green-700' },
  { outcome: 1, label: 'az IFA dátuma nem későbbi, nem változott', tone: 'text-gray-600' },
  { outcome: 2, label: 'nincs IFA-kód rögzítve - kézzel add meg', tone: 'text-amber-700' },
  { outcome: 3, label: 'nem szerepel az IFA keretében', tone: 'text-amber-700' },
  { outcome: 4, label: 'az IFA-ban sincs sportorvosi dátum', tone: 'text-amber-700' },
];

const BATCH = 50;

/**
 * Sportorvosi ellenőrzés nagy létszámra: állapot szerinti fülek, keresés,
 * fülenkénti "mind kijelölése", közös dátum és IFA frissítés egy kérésben.
 * A kijelölés fülváltáskor megmarad, így több fülről is összeszedhető.
 */
const SportDoctorCheckModal: React.FC<SportDoctorCheckModalProps> = ({ isOpen, onClose, players, canModify, onChanged }) => {
  const { showToast } = useToast();
  const warningDays = useSportDoctorWarningDays();
  const [segment, setSegment] = useState<Segment>('problem');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkDate, setBulkDate] = useState('');
  const [rowDates, setRowDates] = useState<Record<number, string>>({});
  const [savingBulk, setSavingBulk] = useState(false);
  const [savingRowId, setSavingRowId] = useState<number | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SportDoctorSyncResult | null>(null);

  const handleClose = () => {
    setSelected(new Set());
    setRowDates({});
    setBulkDate('');
    setSearch('');
    setSyncResult(null);
    onClose();
  };

  const stateById = useMemo(
    () => new Map(players.map((p) => [p.id, getMedicalState(p, warningDays)])),
    [players, warningDays]
  );

  const bySegment = useMemo(() => {
    const result: Record<Segment, Player[]> = { problem: [], expiring: [], valid: [] };
    for (const p of players) {
      const state = stateById.get(p.id)!;
      const seg = SEGMENTS.find((s) => s.states.includes(state))!.id;
      result[seg].push(p);
    }
    // A legsürgősebb elöl: a lejáratuk szerint, a hiányzók a lejártak előtt
    const time = (p: Player) => (p.sportDoctorValidity ? new Date(p.sportDoctorValidity).getTime() : -Infinity);
    (Object.keys(result) as Segment[]).forEach((k) => result[k].sort((a, b) => time(a) - time(b)));
    return result;
  }, [players, stateById]);

  // Megnyitáskor a legelső nem üres fülre állunk
  useEffect(() => {
    if (!isOpen) return;
    setSegment(bySegment.problem.length > 0 ? 'problem' : bySegment.expiring.length > 0 ? 'expiring' : 'valid');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Escape zárja az ablakot
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && handleClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const rows = useMemo(
    () =>
      bySegment[segment].filter((p) =>
        matchesSearch(search, p.name, p.taj, p.ifaCode, (p.ageGroups ?? []).join(' '), p.ageGroup)
      ),
    [bySegment, segment, search]
  );
  const { visible, hasMore, loadMore } = useBatchedList(rows, BATCH);

  if (!isOpen) return null;

  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allRowsSelected = rows.length > 0 && rows.every((p) => selected.has(p.id));
  const toggleAllRows = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      rows.forEach((p) => (allRowsSelected ? next.delete(p.id) : next.add(p.id)));
      return next;
    });

  const handleBulkSave = async () => {
    if (selected.size === 0 || !bulkDate) return;
    setSavingBulk(true);
    try {
      const result = await playersAPI.setSportDoctorValidity(Array.from(selected), bulkDate);
      const skipped = result.skippedCount > 0 ? ` (${result.skippedCount} kihagyva jogosultság hiányában)` : '';
      showToast(`${result.updatedCount} játékos sportorvosi dátuma elmentve${skipped}.`, 'success');
      setSelected(new Set());
      setBulkDate('');
      await onChanged();
    } catch (err) {
      showToast(getErrorMessage(err, 'A sportorvosi dátumokat nem sikerült elmenteni.'), 'error');
    } finally {
      setSavingBulk(false);
    }
  };

  const rowDate = (p: Player) => rowDates[p.id] ?? (p.sportDoctorValidity ? p.sportDoctorValidity.slice(0, 10) : '');

  const handleRowSave = async (p: Player) => {
    const date = rowDate(p);
    if (!date) return;
    setSavingRowId(p.id);
    try {
      await playersAPI.update(p.id, { sportDoctorValidity: date });
      setRowDates((prev) => {
        const next = { ...prev };
        delete next[p.id];
        return next;
      });
      await onChanged();
    } catch (err) {
      showToast(getErrorMessage(err, 'A sportorvosi dátumot nem sikerült elmenteni.'), 'error');
    } finally {
      setSavingRowId(null);
    }
  };

  /**
   * A kijelöltek dátuma az IFA-ból. A kézi megadás mellett él, nem helyette: ha
   * nincs IFA-kód vagy az IFA-ban nincs dátum, a soronkénti mentés járható út.
   */
  const handleSync = async () => {
    if (selected.size === 0) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      const result = await mlszAPI.syncSportDoctor(Array.from(selected));
      setSyncResult(result);
      if (result.updatedCount > 0) {
        setSelected(new Set());
        await onChanged();
      }
    } catch (err: any) {
      setSyncResult({
        success: false,
        error: err.response?.data?.error || err.response?.data?.message || 'Az IFA lekérdezés nem sikerült.',
        updatedCount: 0,
        players: [],
      });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-stretch justify-center md:items-center md:p-4">
      <div className="absolute inset-0 bg-black/30" onClick={handleClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="sport-doctor-title"
        className="relative z-50 flex w-full max-w-3xl flex-col bg-white shadow-xl md:max-h-[85vh] md:rounded-lg"
      >
        {/* Fejléc: cím, fülek, kereső, közös műveletek - görgetéskor is látszik */}
        <div className="shrink-0 border-b border-gray-200 p-4 md:p-6 md:pb-4">
          <div className="mb-1 flex items-center justify-between">
            <h2 id="sport-doctor-title" className="text-lg font-semibold text-gray-900">
              Sportorvosi ellenőrzés
            </h2>
            <button
              type="button"
              onClick={handleClose}
              aria-label="Bezárás"
              className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <p className="mb-3 hidden text-sm text-gray-600 sm:block">
            {players.length} aktív játékos a Játékosok oldal jelenlegi szűrése alapján.
          </p>

          <div className="mb-3 flex flex-wrap gap-2" role="tablist">
            {SEGMENTS.map((s) => (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={segment === s.id}
                onClick={() => setSegment(s.id)}
                className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                  segment === s.id
                    ? s.id === 'problem'
                      ? 'bg-red-600 text-white'
                      : s.id === 'expiring'
                        ? 'bg-amber-500 text-white'
                        : 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {s.label} ({bySegment[s.id].length})
              </button>
            ))}
          </div>

          <div className="relative mb-3">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Szűkítés név, TAJ, IFA kód vagy korosztály szerint..."
              aria-label="Keresés a listában"
              className="block w-full rounded-md border-gray-300 pl-10 text-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          {canModify && (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-gray-200 bg-gray-50 p-3">
              <span className="text-sm font-medium text-gray-900">{selected.size} kijelölve</span>
              {selected.size > 0 && (
                <button type="button" onClick={() => setSelected(new Set())} className="text-xs text-gray-500 hover:text-gray-700">
                  (törlés)
                </button>
              )}
              <span className="mx-1 hidden h-5 w-px bg-gray-300 sm:block" />
              <label className="flex items-center gap-2 text-sm">
                <span className="text-gray-700">Közös dátum:</span>
                <input
                  type="date"
                  value={bulkDate}
                  onChange={(e) => setBulkDate(e.target.value)}
                  className="rounded border-gray-300 px-2 py-1 text-sm"
                />
              </label>
              <button
                type="button"
                disabled={selected.size === 0 || !bulkDate || savingBulk}
                onClick={handleBulkSave}
                className="rounded bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {savingBulk ? 'Mentés...' : 'Mentés a kijelölteknek'}
              </button>
              <button
                type="button"
                disabled={selected.size === 0 || syncing}
                onClick={handleSync}
                className="rounded border border-primary-600 px-3 py-1.5 text-sm font-medium text-primary-700 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {syncing ? 'Lekérdezés az IFA-ból...' : 'Frissítés IFA-ból'}
              </button>
              <p className="hidden w-full text-xs text-gray-500 sm:block">
                A kijelölés fülváltáskor megmarad. Az érvényes sportorvosival rendelkezők is frissíthetők - pl. ha a
                lejárat előtt elmentek az új vizsgálatra.
              </p>
            </div>
          )}

          {syncResult && <SyncSummary result={syncResult} />}
        </div>

        {/* Lista */}
        <div className="min-h-0 flex-1 overflow-y-auto p-4 md:px-6">
          {rows.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500">
              {search ? 'Nincs a keresésnek megfelelő játékos ezen a fülön.' : 'Ezen a fülön nincs játékos.'}
            </p>
          ) : (
            <>
              {canModify && (
                <div className="mb-2 flex items-center justify-between text-sm">
                  <label className="flex items-center gap-2 font-medium text-gray-700">
                    <input
                      type="checkbox"
                      checked={allRowsSelected}
                      onChange={toggleAllRows}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600"
                    />
                    {allRowsSelected ? 'Kijelölés törlése' : `Mind a ${rows.length} kijelölése`}
                  </label>
                  <span className="hidden text-xs text-gray-500 sm:inline">lejárat szerint, a legsürgősebb elöl</span>
                </div>
              )}
              <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
                {visible.map((p) => {
                  const badge = medicalBadge(p, stateById.get(p.id)!);
                  const date = rowDate(p);
                  const unchanged = date === (p.sportDoctorValidity ?? '').slice(0, 10);
                  return (
                    <li key={p.id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2">
                      {canModify && (
                        <input
                          type="checkbox"
                          checked={selected.has(p.id)}
                          onChange={() => toggle(p.id)}
                          aria-label={`${p.name} kijelölése`}
                          className="h-4 w-4 shrink-0 rounded border-gray-300 text-primary-600"
                        />
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-gray-900">{p.name}</span>
                        <span className="block text-xs text-gray-500">
                          {(p.ageGroups?.length ? p.ageGroups : [p.ageGroup]).join(', ')}
                          {!p.ifaCode && <span className="ml-2 text-amber-700">nincs IFA-kód</span>}
                        </span>
                      </span>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}>
                        {badge.text}
                      </span>
                      {canModify && (
                        <span className="flex shrink-0 items-center gap-2">
                          <input
                            type="date"
                            value={date}
                            onChange={(e) => setRowDates((prev) => ({ ...prev, [p.id]: e.target.value }))}
                            aria-label={`${p.name} sportorvosi érvényessége`}
                            className="w-[140px] rounded border-gray-300 px-2 py-1 text-sm"
                          />
                          <button
                            type="button"
                            disabled={savingRowId === p.id || !date || unchanged}
                            onClick={() => handleRowSave(p)}
                            className="rounded bg-primary-600 px-2 py-1 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-40"
                          >
                            {savingRowId === p.id ? '...' : 'Mentés'}
                          </button>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
              {hasMore && <LazyLoadTrigger key={visible.length} onLoadMore={loadMore} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/** Az IFA frissítés eredménye összesítve - 800 sor helyett kimenetenkénti darabszám, lenyitható névsorral. */
const SyncSummary: React.FC<{ result: SportDoctorSyncResult }> = ({ result }) => {
  if (!result.success) {
    return <p className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{result.error}</p>;
  }
  return (
    <div className="mt-3 rounded-md border border-gray-200 bg-white p-3 text-sm">
      <p className="font-medium text-gray-900">
        {result.updatedCount > 0
          ? `${result.updatedCount} játékos dátuma frissült az IFA-ból.`
          : 'Egy játékos dátuma sem változott.'}
      </p>
      <ul className="mt-1 space-y-1">
        {SYNC_OUTCOMES.map(({ outcome, label, tone }) => {
          const rows = result.players.filter((r) => r.outcome === outcome);
          if (rows.length === 0) return null;
          return (
            <li key={outcome}>
              <details>
                <summary className={`cursor-pointer ${tone}`}>
                  {rows.length} játékos: {label}
                </summary>
                <p className="mt-1 pl-4 text-xs text-gray-600">
                  {rows
                    .map((r) =>
                      r.outcome === 0 && r.ifaValidity
                        ? `${r.playerName} (${new Date(r.ifaValidity).toLocaleDateString('hu-HU')})`
                        : r.playerName
                    )
                    .join(', ')}
                </p>
              </details>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default SportDoctorCheckModal;
